import { FC, useEffect } from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';

// Components
import { defineMessages, useIntl } from 'dibs-react-intl';
import PrintIcon from 'dibs-icons/exports/legacy/Print';
import { Link } from 'dibs-elements/exports/Link';
import { PdpRespIconBubbleWithTooltip } from './PdpRespIconBubbleWithTooltip';

// redux actions
import { printActions } from '../../actions/pdpRespPrintActions';

// helpers
import { trackPrintNoPriceClick, trackPrintWithPriceClick } from '../tracking/iconBubblesTracking';
import { usePrevious } from 'dibs-react-hooks/exports/usePrevious';

import styles from './PdpRespIconBubbles.scss';

// messages
const messages = defineMessages({
    print: { id: 'PdpRespIconBubblePrint.print', defaultMessage: 'Print' },
    printNoPrice: {
        id: 'PdpRespIconBubblePrint.printNoPrice',
        defaultMessage: 'Print without Price',
    },
    printTrade: { id: 'PdpRespIconBubblePrint.printTrade', defaultMessage: 'Print Tear Sheet' },
    printNoPriceTrade: {
        id: 'PdpRespIconBubblePrint.printNoPriceTrade',
        defaultMessage: 'Print Tear Sheet without Price',
    },
});

type PrintCopyProps = { isTrade?: boolean; showPrice?: boolean };

type MappedStateProps = {
    pdpPrint: {
        triggerWindowPrint: boolean;
    };
};

type MappedDispatchProps = {
    updateToPrintWithPrice: () => void;
    updateToPrintWithoutPrice: () => void;
    updateTriggerWindowPrint: () => void;
};

type OwnProps = {
    isTrade?: boolean;
};

type Props = MappedStateProps & MappedDispatchProps & OwnProps;

const PrintCopy: FC<PrintCopyProps> = ({ isTrade, showPrice }) => {
    const intl = useIntl();
    let message: keyof typeof messages;
    if (showPrice) {
        message = isTrade ? 'printTrade' : 'print';
    } else {
        message = isTrade ? 'printNoPriceTrade' : 'printNoPrice';
    }
    return <>{intl.formatMessage(messages[message])}</>;
};

const PdpRespIconBubblePrintComponent: FC<Props> = ({
    pdpPrint: { triggerWindowPrint },
    updateToPrintWithPrice,
    updateToPrintWithoutPrice,
    updateTriggerWindowPrint,
    isTrade,
}) => {
    const previousTriggerWindowPrint = usePrevious(triggerWindowPrint);

    const intl = useIntl();

    useEffect(() => {
        if (triggerWindowPrint && previousTriggerWindowPrint !== triggerWindowPrint) {
            window.print();
            updateTriggerWindowPrint();
        }
    }, [triggerWindowPrint, previousTriggerWindowPrint, updateTriggerWindowPrint]);

    const tradeDataTnSuffix = isTrade ? '-trade' : '';

    return (
        <PdpRespIconBubbleWithTooltip icon={PrintIcon} label={intl.formatMessage(messages.print)}>
            <Link
                role="menuitem"
                className={styles.printLink}
                onClick={e => {
                    e.stopPropagation();
                    trackPrintWithPriceClick();
                    updateToPrintWithPrice();
                }}
                dataTn={`print${tradeDataTnSuffix}`}
            >
                <PrintCopy isTrade={isTrade} showPrice />
            </Link>
            <Link
                role="menuitem"
                className={styles.printLink}
                onClick={e => {
                    e.stopPropagation();
                    trackPrintNoPriceClick();
                    updateToPrintWithoutPrice();
                }}
                dataTn={`print-no-price${tradeDataTnSuffix}`}
            >
                <PrintCopy isTrade={isTrade} />
            </Link>
        </PdpRespIconBubbleWithTooltip>
    );
};

// Redux Map Functions
const mapStateToProps: MapStateToProps<MappedStateProps, OwnProps, MappedStateProps> = ({
    pdpPrint,
}) => ({ pdpPrint });

const mapDispatchToProps: MapDispatchToProps<MappedDispatchProps, OwnProps> = dispatch => ({
    updateToPrintWithPrice: () => dispatch(printActions.printWithPrice()),
    updateToPrintWithoutPrice: () => dispatch(printActions.printWithoutPrice()),
    updateTriggerWindowPrint: () => dispatch(printActions.windowPrintTriggered()),
});

export const PdpRespIconBubblePrint = connect(
    mapStateToProps,
    mapDispatchToProps
)(PdpRespIconBubblePrintComponent);
